import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

// Utils
import { useAppDispatch, useAppState } from '../context/AppContext'
import {
  getNotesForCompany,
  getUnreadNotes,
  getViewableAudiences,
  resolveReleaseNoteTerms,
  shouldAutoOpen,
} from '../common/helpers/releaseNotes.helper'
import { isSupport } from '../common/helpers/isLeadOf'
import allReleaseNotes from '../common/releaseNotes'
import syncReleaseNotes from '../context/services/releaseNotes/syncReleaseNotes.service'
import markReleaseNoteOpened from '../context/services/releaseNotes/markReleaseNoteOpened.service'

// Types
import {
  IReleaseNote,
  IReleaseNoteContent,
  IReleaseNoteItem,
  ReleaseNoteAudience,
} from '../common/models/ReleaseNote'

// Release notes for the current member in the current company. Pass `sync` from
// the single place that should fetch the state (the popup mounted by AppLayout),
// and `viewAs` to read the notes of a role below the member's own.
const useReleaseNotes = (sync = false, viewAs?: ReleaseNoteAudience) => {
  const { company, user, userAuthToken, releaseNotes } = useAppState()
  const dispatch = useAppDispatch()
  const { i18n, t } = useTranslation()

  const companyId = company?._id ? '' + company._id : undefined
  const synced =
    releaseNotes && releaseNotes.companyId === companyId
      ? releaseNotes
      : undefined
  const state = synced?.state || undefined
  // Support is never notified and keeps no state, but can read the admin notes
  const ownAudience =
    state?.audience ||
    (isSupport(user?.asCompanyMember) ? ReleaseNoteAudience.ADMIN : undefined)
  const audiences = getViewableAudiences(ownAudience)
  const audience = viewAs && audiences.includes(viewAs) ? viewAs : ownAudience

  useEffect(() => {
    if (!sync || !companyId || !userAuthToken || synced) return
    syncReleaseNotes(userAuthToken)
      .then((memberState) =>
        dispatch({
          type: 'updateReleaseNotes',
          payload: { companyId, state: memberState, autoOpened: false },
        })
      )
      .catch(() => undefined)
  }, [sync, companyId, userAuthToken, !!synced])

  // Drafts are for support only, so a note can be read before its release
  const includeDrafts = isSupport(user?.asCompanyMember)

  const notes = useMemo(
    () =>
      getNotesForCompany(
        allReleaseNotes,
        audience,
        company?.settings,
        new Date(),
        includeDrafts
      ),
    [audience, company?.settings, includeDrafts]
  )

  const unreadIds = getUnreadNotes(notes, state).map((note) => note.id)

  const markOpened = (noteIds: string[]) => {
    if (!synced || !state || !userAuthToken) return
    const newIds = noteIds.filter((id) => !state.openedIds.includes(id))
    if (!newIds.length) return
    dispatch({
      type: 'updateReleaseNotes',
      payload: {
        ...synced,
        state: { ...state, openedIds: [...state.openedIds, ...newIds] },
      },
    })
    markReleaseNoteOpened(userAuthToken, newIds).catch(() => undefined)
  }

  const language: 'nl' | 'en' = i18n.language?.startsWith('nl') ? 'nl' : 'en'

  // The note's content for this audience and language, with {{term:…}} tokens
  // resolved through t() so company terminology overrides apply
  const contentOf = (
    note: IReleaseNote
  ): (IReleaseNoteContent & { illustration?: string }) | undefined => {
    const variant = audience && note.variants[audience]
    const content = variant?.[language]
    if (!variant || !content) return undefined
    const resolve = (text: string) =>
      resolveReleaseNoteTerms(text, (key) => t(key as any))
    const resolveItems = (items?: IReleaseNoteItem[]) =>
      items?.map((item) => ({
        ...item,
        title: resolve(item.title),
        body: resolve(item.body),
        points: item.points?.map(resolve),
      }))
    const sections = content.sections || {}
    return {
      label: resolve(content.label),
      title: resolve(content.title),
      intro: resolve(content.intro),
      illustration: variant.illustration,
      sections: {
        new: resolveItems(sections.new),
        improved: resolveItems(sections.improved),
        fixed: resolveItems(sections.fixed),
      },
    }
  }

  const markAutoOpened = () =>
    synced &&
    dispatch({
      type: 'updateReleaseNotes',
      payload: { ...synced, autoOpened: true },
    })

  return {
    audience,
    audiences,
    notes,
    unreadIds,
    autoOpen: !!synced && !synced.autoOpened && shouldAutoOpen(notes, state),
    markAutoOpened,
    open: (noteId: string) => markOpened([noteId]),
    markAllRead: () => markOpened(unreadIds),
    language,
    contentOf,
  }
}

export default useReleaseNotes
